import { ContentContainer } from '@/components/ContentContainer';
import { CreateLabel } from '@/components/CreateLabel';
import { CustomSelectItem } from '@/components/CustomSelect';
import { StepHeader } from '@/components/StepHeader';
import { CustomAutoComplete } from '@/components/ui/Autocomplete';
import { Input } from '@/components/ui/Input';
import { WhyDoWeAsk } from '@/components/WhyDoWeAsk';
import { whyActivateContent } from '@/content/whyActivateContent';
import { getStepContent } from '@/utils/getStepContent';
import { getStepName } from '@/utils/stepUtils';
import { Flex, Grid, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/router';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { PrimaryButton } from '../components/PrimaryButton';
import { StepForm } from '../components/StepForm';
import { StepSubtitle } from '../components/StepSubtitle';
import { StepTitle } from '../components/StepTitle';
import { choiceAndOpenStepContent } from '../content/choiceAndOpenStepContent';
import { openStepRoute } from '../router/router';
import { FieldName, ProviderDropdownItem, StateDropdownItem, Step } from '../state/state.types';
import { useMdBreakpoint } from '../theme/hooks/useMdBreakpoint';
import { useCaseUpdater } from './hooks/useCaseUpdater';
import { useOpenStepResponse } from './hooks/useOpenStepResponse';
import { useProviderDropdown } from './hooks/useProviderDropdown';
import { useStateDropdown } from './hooks/useStateDropdown';

const providerInput = z.object({
  [FieldName.medical_provider_name]: z.string().nonempty('Please select a provider name'),
  [FieldName.state]: z.string().nonempty('Please select a state'),
  [FieldName.city]: z.string().nonempty('Please enter a city'),
  [FieldName.from_dropdown]: z.string().optional(),
});

type ProviderInput = z.infer<typeof providerInput>;

const MedicalProviderName = () => {
  const isMdBreakpoint = useMdBreakpoint();
  const [isOther, setIsOther] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(searchTerm, 500, { leading: true });

  const form = useForm<ProviderInput>({
    validate: zodResolver(providerInput),
    validateInputOnBlur: true,
    initialValues: {
      [FieldName.medical_provider_name]: '',
      [FieldName.city]: '',
      [FieldName.state]: '',
      [FieldName.from_dropdown]: '',
    },
  });

  useEffect(() => {
    const fromDropdown = form.values['from-dropdown'];
    const providerName = form.values['medical-provider-name'];

    if (fromDropdown === 'false') {
      setIsOther(providerName);
    }
  }, [form.values['from-dropdown']]);

  const { stepId } = useParams({ from: openStepRoute.id });
  const { data: step } = useQuery<Step>({
    queryKey: ['step', stepId],
    enabled: !!stepId,
  });

  const { providerDropdown_: dropdownData, isLoading: providerDataLoading } = useProviderDropdown({
    search: debouncedSearchValue,
    value: form.values['medical-provider-name'],
  });
  const { stateDropdown_: stateDropdownData, isLoading: stateDataLoading } = useStateDropdown();

  const formTransformer = () => {
    const validatedValues = providerInput.parse(form.values);
    const formData = new FormData();
    formData.append('stepId', stepId);
    formData.append(FieldName.medical_provider_name, validatedValues['medical-provider-name']);
    formData.append(FieldName.city, validatedValues[FieldName.city]);
    formData.append(FieldName.state, validatedValues[FieldName.state]);
    formData.append(FieldName.from_dropdown, !isOther ? 'true' : 'false');

    return formData;
  };

  const { onFormSubmit, isLoading, error } = useCaseUpdater({
    formTransformer,
  });

  const stepName = getStepName(stepId);

  const openStepFieldList = useMemo(() => {
    return [
      { name: providerInput.keyof().options[0] },
      { name: providerInput.keyof().options[1] },
      { name: providerInput.keyof().options[2] },
      { name: providerInput.keyof().options[3] },
    ];
  }, []);

  useOpenStepResponse<ProviderInput>(openStepFieldList, form.setFieldValue);

  if (!stepId || !stepName) throw new Error('StepId or stepName should be defined');

  const { title, subtitle, field, header } = getStepContent(stepId, choiceAndOpenStepContent);

  const { title: whyActivateTitle, description: whyActivateDescription } =
    whyActivateContent?.[stepId] ?? {};

  const isMedicalProviderName = stepId === FieldName.medical_provider_name;

  const stackWidth = 516;

  /**
   * Todo:- What can you do is make a generalised function which take data array and query and gives you output of that obj
   */
  const handleChange = (query: string | null) => {
    const selectedHospital = dropdownData?.filter((val) => val.value === query)[0];

    if (selectedHospital) {
      const city = selectedHospital?.city ?? '';
      const state = selectedHospital?.state_code ?? '';
      form.setFieldValue(FieldName.city, city);
      form.setFieldValue(FieldName.state, state);
    }

    if (query) {
      form.setFieldValue(FieldName.medical_provider_name, query);
    }
  };

  return (
    <Flex
      direction="column"
      h="100%"
    >
      {!!header && <StepHeader>{header}</StepHeader>}
      {!!title &&
        (typeof title === 'string' ? (
          <StepTitle fz={{ base: 28, md: 32 }}>{title}</StepTitle>
        ) : (
          title()
        ))}
      {!!subtitle && <StepSubtitle>{subtitle}</StepSubtitle>}
      <ContentContainer>
        <StepForm onSubmit={onFormSubmit}>
          <input
            name="stepId"
            type="hidden"
            value={stepId}
          />
          <Flex direction="column">
            {!!field && (
              <Stack
                spacing={4}
                sx={{ flexGrow: 1 }}
                style={{
                  maxWidth: stackWidth,
                  minWidth: isMdBreakpoint ? stackWidth : 'auto',
                  margin: isMedicalProviderName ? '0 auto' : 'none',
                }}
              >
                {!isOther && (
                  <CustomAutoComplete
                    error={error?.message}
                    placeholder="Enter Hospital Name"
                    searchable
                    creatable
                    isLoading={providerDataLoading}
                    data={(dropdownData ?? []) as ProviderDropdownItem[]}
                    dropdownComponent="div"
                    itemComponent={CustomSelectItem}
                    getCreateLabel={(query) => <CreateLabel label={query} />}
                    rightSection={<></>}
                    clearable
                    onCreate={(query) => {
                      setIsOther(query);
                      return query;
                    }}
                    onSearchChange={setSearchTerm}
                    searchValue={searchTerm}
                    disabled={isLoading}
                    {...form.getInputProps(FieldName.medical_provider_name)}
                    onChange={handleChange}
                  />
                )}
                {isOther && (
                  <>
                    <Grid
                      mt="md"
                      gutter="md"
                    >
                      <Grid.Col>
                        <Input
                          type="text"
                          name={FieldName.medical_provider_name}
                          inputProps={form.getInputProps(FieldName.medical_provider_name)}
                          error={form.errors['medical_provider_name']}
                          placeholder=""
                          label="Hospital name"
                          isLoading={isLoading}
                        />
                      </Grid.Col>
                      <Grid.Col span={8}>
                        <Input
                          type="text"
                          name={FieldName.city}
                          inputProps={form.getInputProps('city')}
                          error={form.errors['city']}
                          placeholder=""
                          isLoading={isLoading}
                          label="City"
                        />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <CustomAutoComplete
                          label="State"
                          data={(stateDropdownData ?? []) as StateDropdownItem[]}
                          placeholder="State"
                          searchable
                          dropdownComponent="div"
                          itemComponent={CustomSelectItem}
                          error={form.errors['state']}
                          isLoading={stateDataLoading}
                          disabled={isLoading}
                          {...form.getInputProps('state')}
                        />
                      </Grid.Col>
                    </Grid>
                  </>
                )}
                {!!field.helperText && step?.optional && (
                  <Text
                    size={14}
                    align="left"
                    c="primary.1"
                    weight="normal"
                    style={{
                      margin: '4px auto 0 auto',
                    }}
                  >
                    {field.helperText}
                  </Text>
                )}
              </Stack>
            )}
            <PrimaryButton
              isLoading={isLoading}
              isDisabled={
                !form.isValid(FieldName.medical_provider_name) ||
                !form.isValid(FieldName.city) ||
                !form.isValid(FieldName.state)
              }
              mt={24}
              align={
                isMdBreakpoint ? (field?.inputElement === 'textarea' ? 'end' : 'start') : 'center'
              }
              mx="auto"
              maw={343}
              w="100%"
            >
              Continue
            </PrimaryButton>
          </Flex>
        </StepForm>
      </ContentContainer>
      {whyActivateTitle && whyActivateDescription && (
        <Flex
          align="center"
          justify="center"
          mt="auto"
        >
          <WhyDoWeAsk
            title={whyActivateTitle}
            description={whyActivateDescription}
            stepId={stepId}
          />
        </Flex>
      )}
    </Flex>
  );
};

export { MedicalProviderName };
